import React from "react";

import { SVGIconPinterestBox } from "components/SVGIconPinterestBox";
import { LEAD_MEDIA_SMALL_WIDTH, LEAD_MEDIA_LARGE_WIDTH } from "utils/constants";
import { JWVideoModule } from "components/JWVideoModule";
import { ResponsiveImage } from "components/ResponsiveImage";
import { EmbedModule, EmbedContent } from "components/EmbedModule";
import { useAppState } from "hooks/AppContext";

import styles from "./leadMedia.module.scss";

export interface LeadMedia {
    altText?: string;
    caption?: string;
    credit?: string;
    creditLink?: string;
    embed?: EmbedContent;
    height?: number;
    id?: number;
    jwVideo?: string;
    showPinterest?: boolean;
    type: string;
    url?: string;
    width?: number;
}

export interface LeadMediaProps extends LeadMedia {
    className?: string;
}

const getPinUrl = (href: string, media: string, description: string): string => {
    const page = encodeURIComponent(href);
    const image = encodeURIComponent(media);
    const alt = encodeURIComponent(description);
    return `https://www.pinterest.com/pin/create/button/?url=${page}&media=${image}&description=${alt}`;
};

const Pinterest = (alt: string, url: string, href: string) => (
    <div className={styles.imagePin}>
        <div>
            <a
                href={getPinUrl(href, url, alt)}
                rel="nofollow noreferrer"
                target="_blank"
                className={`${styles.button} ${styles.socialIcon} ${styles.pinterest}`}
            >
                <SVGIconPinterestBox />
            </a>
        </div>
    </div>
);

const RenderLeadImage = (props: LeadMedia) => {
    const {
        height = 100, width = 100, url, altText, credit, creditLink, caption
    } = props;
    const aspect = height / width;
    const mobileWidth = LEAD_MEDIA_SMALL_WIDTH;
    const tabletWidth = LEAD_MEDIA_LARGE_WIDTH;
    const mobileHeight = Math.ceil(mobileWidth * aspect);
    const tabletHeight = Math.ceil(tabletWidth * aspect);
    const extraProps = {
        fetchpriority: "high"
    };
    const leadImageProps = {
        altText,
        caption,
        className: styles.imageResponsive,
        // The following linter issue is a bug in eslint
        // eslint-disable-next-line no-undef
        loading: "eager" as const,
        credit,
        creditLink,
        mobileHeight,
        mobileWidth,
        tabletWidth,
        tabletHeight,
        url,
        extraProps
    };
    return <ResponsiveImage {...leadImageProps} />;
};

const RenderEmbedVideo = (props: LeadMedia) => <EmbedModule type="embedBlock" embed={props} template="leadMedia" />;

const RenderJWVideo = (props: LeadMedia) => {
    const { jwVideo } = props;
    return (
        <JWVideoModule
            type="jwVideoBlock"
            jwVideo={jwVideo}
            autostart={true}
            position="post_individual_header"
        />
    );
};

export const LeadMediaModule = (props: LeadMediaProps) => {
    const { pageMetaData } = useAppState();
    const { href } = pageMetaData;
    const {
        altText, showPinterest, type, url
    } = props;

    // The following linter issue is a bug in eslint
    // eslint-disable-next-line no-undef
    const renderMethod: { [key: string]: (childProps: LeadMedia) => JSX.Element } = {
        image: RenderLeadImage,
        embedVideo: RenderEmbedVideo,
        jwVideo: RenderJWVideo
    };

    const getLead = renderMethod[type];

    return (
        <figure className={styles.leadMedia}>
            {getLead && getLead(props)}
            {showPinterest && Pinterest(altText, url, href)}
        </figure>
    );
};
